import React, { FC } from 'react';

import { Button, Container, ContainerProps } from '@lglp/components';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { COMMON } from 'translations';

import Link from '../../../components/library/Link';
import { getUrlForRouteKey, ROUTE_KEY } from '../../../config/routes';
import { getMessage } from '../../../helpers/error';
import {
  HTTP_CLIENT_ERROR_NOT_FOUND,
  HTTP_SERVER_SERVICE_UNAVAILABLE,
  HTTP_SERVER_GATEWAY_TIMEOUT,
} from '../../../helpers/http';
import { NOINDEX } from '../../../helpers/seo';
import { LinkWeb, LinkType } from '../../../types';
import { CTAArrow } from '../../Icons';
import { ArrowOrientation } from '../../Icons/Arrow';
import ErreurIcon from '../../Icons/ErreurIcon';
import Error404Icon from '../../Icons/Error404Icon';
import Error504Icon from '../../Icons/Error504Icon';
import MaintenanceIcon from '../../Icons/MaintenanceIcon';
import styles from './ErrorState.module.scss';

type ErrorStateProps = ContainerProps & {
  error: any;
};

const ErrorStatusIcon: FC<ContainerProps & { status: any }> = ({
  status,
  className,
  ...rest
}) => {
  if (status === HTTP_CLIENT_ERROR_NOT_FOUND) {
    return <Error404Icon className={className} {...rest} />;
  }

  if (status === HTTP_SERVER_GATEWAY_TIMEOUT) {
    return <Error504Icon className={className} {...rest} />;
  }

  if (status === HTTP_SERVER_SERVICE_UNAVAILABLE) {
    return (
      <MaintenanceIcon
        className={[styles.maintenanceIcon, className]}
        {...rest}
      />
    );
  }

  return <ErreurIcon className={className} />;
};

export const ErrorState: FC<ErrorStateProps> = ({
  className,
  error,
  ...rest
}) => {
  // const history = useHistory();
  const { t, i18n } = useTranslation();
  const status = Number(error?.status);
  const message = getMessage(t, error);

  const link: LinkWeb = {
    url: getUrlForRouteKey(ROUTE_KEY.HOME, i18n.language),
    linkType: LinkType.LINK_TYPE_WEB,
  };

  return (
    <Container className={[styles.background, className]} {...rest}>
      <Helmet>
        <meta name="robots" content={NOINDEX} />
      </Helmet>

      <Container className={[styles.container, className]} {...rest}>
        <ErrorStatusIcon className={styles.icon} status={status} />
        <div className={styles.message}>
          <p>{message}</p>
          {status !== HTTP_SERVER_SERVICE_UNAVAILABLE && (
            <Button
              as={Link}
              className={[styles.backCta, 'animate-svg']}
              {...link}
            >
              <CTAArrow orientation={ArrowOrientation.LEFT} />
              <Button.Label>{t(COMMON.ERROR_BACK_TO_HOME)}</Button.Label>
            </Button>
          )}
        </div>
      </Container>
    </Container>
  );
};
